import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface FeedbackFormProps {
  // 当前登录用户邮箱，可预填
  email?: string | null;
}

const categories = [
  "账户问题",
  "功能反馈",
  "问题报告",
  "乐透问题",
  "手机号码验证",
  "其他",
] as const;

type Category = (typeof categories)[number];

const FeedbackForm: React.FC<FeedbackFormProps> = ({ email }) => {
  const [category, setCategory] = React.useState<Category | "">("");
  const [description, setDescription] = React.useState("");
  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [userEmail, setUserEmail] = React.useState(email ?? "");

  const mutation = useMutation<{ success: boolean }, Error, FormData>({
    mutationFn: async (formData: FormData) => {
      const res = await fetch("/feedbacks", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      if (!res.ok) throw new Error("提交失败，请稍后再试");
      return res.json();
    },
    onSuccess: () => {
      toast.success("反馈已发送，感谢您的宝贵意见！");
      // Reset form
      setCategory("");
      setDescription("");
      setAttachment(null);
    },
    onError: (error) => {
      toast.error(error.message || "提交失败，请稍后再试");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) {
      toast.error("请选择问题类型");
      return;
    }
    if (!description.trim()) {
      toast.error("请填写问题描述");
      return;
    }
    const formData = new FormData();
    formData.append("category", category);
    formData.append("description", description);
    formData.append("email", userEmail);
    if (attachment) formData.append("attachment", attachment);
    mutation.mutate(formData);
  };

  return (
    <div className="flex justify-center px-4 py-0 lg:py-8">
      <Card className="w-full max-w-3xl p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* 问题类型 */}
          <div>
            <label className="mb-2 block text-sm font-medium">问题类型</label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as Category)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="请选择问题类型" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 问题描述 */}
          <div>
            <label className="mb-2 block text-sm font-medium">问题描述</label>
            <Textarea
              className="text-sm"
              rows={6}
              placeholder="请告诉我们你尝试执行的操作、注意到的意外行为，以及是否看到任何错误消息。"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* 附件上传 */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              附件（可选）
            </label>
            <label
              htmlFor="attachment"
              className={cn(
                "flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/40 text-muted-foreground hover:bg-muted/50"
              )}
            >
              <span className="text-2xl">＋</span>
              <input
                id="attachment"
                type="file"
                className="hidden"
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              />
            </label>
            {attachment && (
              <p className="mt-2 text-sm text-muted-foreground truncate max-w-xs">
                已选择：{attachment.name}
              </p>
            )}
          </div>

          {/* 邮箱 */}
          <div>
            <label className="mb-2 block text-sm font-medium">你的邮箱</label>
            <Input
              type="email"
              required
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "发送中..." : "发送"}
          </Button>
        </form>

        {/* 联系我们 */}
        <div className="mt-4 text-sm  text-gray-500">
          <p>
            你也可以通过电子邮件联系我们:{" "}
            <a
              href="mailto:contact@tunzo.cn"
              className="text-primary text-gray-600 font-bold"
            >
              contact@tunzo.cn
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default FeedbackForm;
